/**
 * External dependencies
 */
import clsx from 'clsx';
import type { Maybe } from '@nab/types';

/**
 * WordPress dependencies
 */
import {
	TextControl,
	VStack,
	Heading,
	HStack,
	DropdownContentWrapper,
	Button,
	FlexItem,
	Dropdown,
	Composite,
	Tooltip,
} from '@safe-wordpress/components';
import { useMemo, useRef } from '@safe-wordpress/element';
import {
	shadow as shadowIcon,
	Icon,
	check,
	reset,
} from '@safe-wordpress/icons';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import type { FullEditorSettings, Shadow } from '../../utils';

/**
 * Shared reference to an empty array for cases where it is important to avoid
 * returning a new array reference on every invocation.
 *
 * @type {Array}
 */
const EMPTY_ARRAY: Array< Shadow > = [];

type ShadowPopoverContainerProps = {
	readonly value?: string;
	readonly inheritedValue?: string;
	readonly settings: FullEditorSettings;
	readonly disabled: boolean;
	readonly onChange: ( newValue?: string ) => void;
};

export function ShadowPopoverContainer( {
	value,
	inheritedValue,
	settings,
	disabled,
	onChange,
}: ShadowPopoverContainerProps ): JSX.Element {
	const shadows = useShadowPresets( settings );

	const actualValue = value || inheritedValue;
	const isPreset =
		! actualValue ||
		shadows.some( ( { shadow } ) => shadow === actualValue );

	if ( disabled && ! isPreset ) {
		return (
			<div className="block-editor-global-styles__shadow-popover-container">
				<VStack spacing={ 4 }>
					<Heading level={ 5 }>
						{ _x( 'Drop shadow', 'text', 'nelio-ab-testing' ) }
					</Heading>
					<TextControl
						disabled
						onChange={ () => void null }
						value={ value || inheritedValue || '' }
					/>
				</VStack>
			</div>
		);
	}

	return (
		<div className="block-editor-global-styles__shadow-popover-container">
			<VStack spacing={ 4 }>
				<Heading level={ 5 }>
					{ _x( 'Drop shadow', 'text', 'nelio-ab-testing' ) }
				</Heading>
				<ShadowPresets
					presets={ shadows }
					activeShadow={ value || inheritedValue }
					onSelect={ onChange }
					disabled={ disabled }
				/>
				{ ! isPreset && (
					<TextControl
						label={ _x(
							'Current value',
							'text',
							'nelio-ab-testing'
						) }
						disabled
						onChange={ () => void null }
						value={ value || inheritedValue || '' }
					/>
				) }
				{ ! disabled && (
					<div className="block-editor-global-styles__clear-shadow">
						<Button
							__next40pxDefaultSize
							variant="tertiary"
							onClick={ () => onChange( undefined ) }
							disabled={ ! value }
						>
							{ _x( 'Clear', 'text', 'nelio-ab-testing' ) }
						</Button>
					</div>
				) }
			</VStack>
		</div>
	);
}

type ShadowPresetsProps = {
	readonly presets: Shadow[];
	readonly activeShadow?: string;
	readonly disabled: boolean;
	readonly onSelect: ( newValue?: string ) => void;
};
export function ShadowPresets( {
	presets,
	activeShadow,
	disabled,
	onSelect,
}: ShadowPresetsProps ): JSX.Element | null {
	return ! presets ? null : (
		<Composite
			role="listbox"
			className="block-editor-global-styles__shadow__list"
			disabled={ disabled }
			aria-label={ _x( 'Drop shadows', 'text', 'nelio-ab-testing' ) }
		>
			{ presets.map( ( { name, slug, shadow } ) => (
				<ShadowIndicator
					key={ slug }
					label={ name }
					isActive={ shadow === activeShadow }
					type={ slug === 'unset' ? 'unset' : 'preset' }
					onSelect={ () =>
						onSelect( shadow === activeShadow ? undefined : shadow )
					}
					shadow={ shadow }
					disabled={ disabled }
				/>
			) ) }
		</Composite>
	);
}

type ShadowIndicatorProps = {
	readonly type: string;
	readonly label: string;
	readonly isActive: boolean;
	readonly onSelect: () => void;
	readonly shadow: string;
	readonly disabled: boolean;
};
export function ShadowIndicator( {
	type,
	label,
	isActive,
	onSelect,
	shadow,
	disabled,
}: ShadowIndicatorProps ): JSX.Element {
	return (
		<Tooltip text={ label }>
			<Composite.Item
				role="option"
				aria-label={ label }
				aria-selected={ isActive }
				className={ clsx( 'block-editor-global-styles__shadow__item', {
					'is-active': isActive,
				} ) }
				render={
					disabled ? (
						<div
							style={ {
								boxShadow: shadow,
								width: '26px',
								height: '26px',
								border: '1px solid #ddd',
								borderRadius: '2px',
								boxSizing: 'border-box',
							} }
							aria-label={ label }
						>
							{ isActive && <Icon icon={ check } /> }
						</div>
					) : (
						<button
							className={ clsx(
								'block-editor-global-styles__shadow-indicator',
								{
									unset: type === 'unset',
								}
							) }
							onClick={ onSelect }
							style={ { boxShadow: shadow } }
							aria-label={ label }
						>
							{ isActive && <Icon icon={ check } /> }
						</button>
					)
				}
			/>
		</Tooltip>
	);
}

type ShadowPopoverProps = ShadowPopoverContainerProps;
export function ShadowPopover( {
	value,
	inheritedValue,
	settings,
	disabled,
	onChange,
}: ShadowPopoverProps ): JSX.Element {
	const popoverProps = {
		placement: 'left-start',
		offset: 36,
		shift: true,
	};

	return (
		<Dropdown
			popoverProps={ popoverProps }
			className="block-editor-global-styles__shadow-dropdown"
			renderToggle={ renderShadowToggle(
				value,
				inheritedValue,
				onChange
			) }
			renderContent={ () => (
				<DropdownContentWrapper paddingSize="medium">
					<ShadowPopoverContainer
						value={ value }
						inheritedValue={ inheritedValue }
						settings={ settings }
						disabled={ disabled }
						onChange={ onChange }
					/>
				</DropdownContentWrapper>
			) }
		/>
	);
}

function renderShadowToggle(
	shadow: string | undefined,
	inheritedShadow: string | undefined,
	onShadowChange: ( newValue?: string ) => void
) {
	return ( {
		onToggle,
		isOpen,
	}: {
		onToggle: () => void;
		isOpen: boolean;
	} ) => {
		const shadowButtonRef = useRef< Maybe< HTMLElement > >( undefined );

		const toggleProps = {
			onClick: onToggle,
			className: clsx(
				'block-editor-global-styles__shadow-dropdown-toggle',
				{ 'is-open': isOpen }
			),
			'aria-expanded': isOpen,
			ref: shadowButtonRef,
		};

		const removeButtonProps = {
			onClick: () => {
				if ( isOpen ) {
					onToggle();
				}
				onShadowChange( undefined );
				// Return focus to parent button.
				shadowButtonRef.current?.focus();
			},
			className: clsx(
				'block-editor-global-styles__shadow-editor__remove-button',
				{ 'is-open': isOpen }
			),
			label: _x( 'Remove', 'text', 'nelio-ab-testing' ),
		};

		return (
			<>
				<Button __next40pxDefaultSize { ...toggleProps }>
					<HStack justify="flex-start">
						<Icon
							className="block-editor-global-styles__toggle-icon"
							icon={ shadowIcon }
							size={ 24 }
						/>
						<FlexItem>
							{ _x( 'Drop shadow', 'text', 'nelio-ab-testing' ) }
						</FlexItem>
					</HStack>
				</Button>
				{ !! shadow && !! inheritedShadow && (
					<Button
						__next40pxDefaultSize
						size="small"
						icon={ reset }
						{ ...removeButtonProps }
					/>
				) }
			</>
		);
	};
}

export function useShadowPresets(
	settings: FullEditorSettings
): Array< Shadow > {
	return useMemo< Array< Shadow > >( () => {
		if ( ! settings?.shadow ) {
			return EMPTY_ARRAY;
		}

		const defaultPresetsEnabled = settings?.shadow?.defaultPresets;
		const {
			default: defaultShadows,
			theme: themeShadows,
			custom: customShadows,
		} = settings?.shadow?.presets ?? {};
		const unsetShadow = {
			name: _x( 'Unset', 'text', 'nelio-ab-testing' ),
			slug: 'unset',
			shadow: 'none',
		};

		const shadowPresets = [
			...( ( defaultPresetsEnabled && defaultShadows ) || EMPTY_ARRAY ),
			...( themeShadows || EMPTY_ARRAY ),
			...( customShadows || EMPTY_ARRAY ),
		];
		if ( shadowPresets.length ) {
			shadowPresets.unshift( unsetShadow );
		}

		return shadowPresets;
	}, [ settings ] );
}
